import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { ThemeProvider } from 'emotion-theming'
import type { AppProps } from 'next/app'
import STheme, { SStyled, useSTheme } from 'styles/theme'

import { ApolloProvider } from '@apollo/client'
import { useApollo } from 'utils/apolloClient'

import Select from 'react-select'

import Head from 'next/head'

import 'animate.css'
import 'react-datepicker/dist/react-datepicker.css'
import 'react-phone-number-input/style.css'

import { AuthContextProvider } from 'utils/authContext'
import RoutingService from 'utils/routing'

import Fonts from 'utils/fonts'
import Modal from 'react-modal'

import { appWithTranslation, useTranslation } from 'i18n'

import SFlex from 'components/opinionless/SFlex'
import SGlobalStyles from 'styles/globals'
import SIdleTimer from 'components/opinionless/SIdleTimer'
import { useRouter } from 'next/router'

import { PlausibleProviderWrapper } from 'utils/plausible'
import { Intercom, LiveChatLoaderProvider } from 'react-live-chat-loader'
import { isMobile } from 'react-device-detect'

import ConditionalRender from '../utils/conditionalRender'
import SColors from '../styles/colors'

import SLoadingIndicator from '../components/opinionless/SLoadingIndicator'
import { LocalStorageKeys } from 'utils/localStorage'
import SInstallMobile from '../components/opinionless/SInstallMobile'
import { OrganizationConfigsType, OrganizationType, ProgramType, UserType } from '../types/graphql'
import { UserContext } from '../v2/context/user'
import { OrganizationContext } from '../v2/context/organization'
import { TransferMethod } from '../v2/types/transferMethod'
import { UpcomingPayment } from 'v2/types/upcomingPayment'
import TagManager from 'react-gtm-module'

import moment from 'moment'
import { ga4_buttonClicked, ga4_initialize, ga4_listenToRouter } from 'utils/ga4'
import { ErrorBoundary } from '../bugsnag'

const GlobalWrapper = SStyled(SFlex)`
    flex-direction: row;
    min-height: 100vh;
    position: relative;
    overflow-y: auto;
`

const MOBILE_APP_INSTALL_ENABLED = ['cff-2023']

function MyApp(props: AppProps & { props: { [key: string]: string } }) {
    const [authenticatedUser, setAuthenticatedUser] = useState<UserType | undefined>(undefined)
    const [authenticatedUserTransferMethods, setAuthenticatedUserTransferMethods] = useState<TransferMethod[]>([])
    const [authenticatedUserUpcomingTransfer, setAuthenticatedUserUpcomingTransfer] = useState<UpcomingPayment>()
    const [organization, setOrganization] = useState<OrganizationType | undefined>(undefined)
    const [organizationConfigs, setOrganizationConfigs] = useState<OrganizationConfigsType | undefined>(undefined)
    const [program, setProgram] = useState<ProgramType | undefined>(undefined)
    const { Component, pageProps } = props
    const apolloClient = useApollo(pageProps.initialApolloState)
    const [hasLoadedFonts, setHasLoadedFonts] = useState(false)
    const [themeLoaded, setThemeLoaded] = useState(false)
    const [themesToBeSent, setThemesToBeSent] = useState({})
    const [hasLoadedTranslations, setHasLoadedTranslations] = useState(false)
    const [intercomLoaded, setIntercomLoaded] = useState(false)
    const { t } = useTranslation('common')

    const router = useRouter()

    useEffect(() => {
        if (program && !program.isActiveProgram) {
            if (typeof t('resources', { returnObjects: true }) !== 'string') {
                router.push(`/v2${program.urlFragment}/resources`)
            } else {
                router.push(`/v2${program.urlFragment}/help`)
            }
        }

        if (window.location.href.includes('admin') && process.env.NEXT_PUBLIC_ADMIN_ENABLED !== 'yes') {
            window.location.href = window.location.href.split('admin')[0]
        }
    }, [program])

    const theme = useSTheme()
    const devEnvLinks = [
        { value: process.env.NEXT_PUBLIC_GRAPHQL_ENDPOINT, label: 'Dev Env 1' },
        { value: process.env.NEXT_PUBLIC_GRAPHQL_ENDPOINT_2, label: 'Dev Env 2' },
    ]

    if (typeof window !== 'undefined') {
        window.LANG_NS = 'common'
    }

    ga4_initialize(organizationConfigs?.gaMeasurementId)
    ga4_listenToRouter(router)

    if (organizationConfigs?.googleTagManagerId && organizationConfigs?.googleTagManagerId?.trim()?.length > 0) {
        const tagManagerArgs = {
            gtmId: organizationConfigs?.googleTagManagerId,
        }

        if (typeof window !== 'undefined') {
            TagManager.initialize(tagManagerArgs)
        }
    }

    useEffect(() => {
        if (!localStorage.getItem(LocalStorageKeys.GRAPHQL_ENDPOINT)) {
            localStorage.setItem(LocalStorageKeys.GRAPHQL_ENDPOINT, process.env.NEXT_PUBLIC_GRAPHQL_ENDPOINT)
        }
    }, [])

    const onEndpointChange = useCallback((endpoint: any) => {
        localStorage.setItem(LocalStorageKeys.GRAPHQL_ENDPOINT, endpoint.value)
        window.location.reload()
    }, [])

    useEffect(() => {
        RoutingService.pathChanged(router.asPath.replace(window.location.origin, ''))
    }, [router])

    useEffect(() => {
        setThemesToBeSent(STheme)
        setThemeLoaded(true)
    }, [STheme])

    const loadedTranslations = () => {
        setHasLoadedTranslations(true)
    }

    const programFragment = useMemo(() => {
        return router.query?.['program-name']?.toString()
    }, [router])

    useEffect(() => {
        if ('serviceWorker' in navigator && MOBILE_APP_INSTALL_ENABLED.includes(programFragment)) {
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            navigator.serviceWorker.register('/sw.js').then((e) => {})
        }
    }, [programFragment])

    React.useEffect(() => {
        Fonts()
            .catch(() => {
                setHasLoadedFonts(true)
            })
            .finally(() => {
                setHasLoadedFonts(true)
            })
    }, [])

    Modal.setAppElement('#__next')

    useEffect(() => {
        if (intercomLoaded && authenticatedUser) {
            onReady()
        }
    }, [authenticatedUser, authenticatedUser?.person?.profile?.firstName, intercomLoaded])

    const onReady = () => {
        const bootParams = {
            app_id: process.env.NEXT_PUBLIC_INTERCOM_APP_ID,
            hide_default_launcher: false,
        }

        if (authenticatedUser) {
            const personId = atob(authenticatedUser.person.id).split(':')[1]
            bootParams['user_id'] = `${process.env.NEXT_PUBLIC_ENVIRONMENT_NAME}-${personId}`
            bootParams['F4gi id'] = `${process.env.NEXT_PUBLIC_ENVIRONMENT_NAME}-${authenticatedUser.pk}`
            bootParams['email'] = authenticatedUser.email
            bootParams['phone'] = authenticatedUser.communicationSetting.phoneNumber
            bootParams['name'] = authenticatedUser.person?.profile?.firstName
                ? `${authenticatedUser.person?.profile?.firstName} ${authenticatedUser.person?.profile?.lastName}`
                : ''
            bootParams['created_at'] = moment(authenticatedUser.createdAt)?.utc()
            bootParams['user_hash'] = authenticatedUser.person.intercomKey
        }
        window.Intercom('update', bootParams)
        window.Intercom('onShow', () => ga4_buttonClicked('Intercom'))
        window.Intercom('onHide', () => ga4_buttonClicked('Intercom'))
        if (!intercomLoaded) {
            setIntercomLoaded(true)
        }
    }

    useEffect(() => {
        const observer = new MutationObserver(() => {
            const page = document.querySelector('#__next') as HTMLElement
            if (page) {
                const intercomLauncher = page.children[1] as HTMLElement
                if (intercomLauncher) {
                    const loadChatButton = intercomLauncher.querySelector('div[role="button"][aria-label="Load Chat"]')
                    if (loadChatButton) {
                        intercomLauncher.style.bottom = '75px'
                    }
                }
                observer.disconnect()
            }
        })

        observer.observe(document.body, { childList: true, subtree: true })

        return () => {
            observer.disconnect()
        }
    }, [])

    useEffect(() => {
        const observer = new MutationObserver(() => {
            const intercomParent = document.querySelector(
                'div[class*="intercom"] > iframe[name="intercom-launcher-frame"]'
            )?.parentElement
            const intercomLightweightLauncher = document.querySelector('.intercom-lightweight-app-launcher')

            if (intercomParent) {
                intercomParent.style.bottom = '75px'
                observer.disconnect()
            }

            if (intercomLightweightLauncher) {
                ;(intercomLightweightLauncher as HTMLElement).style.bottom = '75px'
            }
        })

        observer.observe(document.body, { childList: true, subtree: true })

        return () => {
            observer.disconnect()
        }
    }, [])

    if (themeLoaded && ErrorBoundary) {
        return (
            <ErrorBoundary>
                <LiveChatLoaderProvider
                    providerKey={process.env.NEXT_PUBLIC_INTERCOM_APP_ID}
                    provider="intercom"
                    onReady={onReady}
                >
                    <ApolloProvider client={apolloClient}>
                        <OrganizationContext.Provider
                            value={{
                                organization: organization,
                                organizationConfigs: organizationConfigs,
                                program: program,
                                setOrganization: setOrganization,
                                setOrganizationConfigs: setOrganizationConfigs,
                                setProgram: setProgram,
                            }}
                        >
                            <UserContext.Provider
                                value={{
                                    transferMethods: authenticatedUserTransferMethods,
                                    authenticatedUser: authenticatedUser,
                                    setAuthenticatedUser: setAuthenticatedUser,
                                    setTransferMethods: setAuthenticatedUserTransferMethods,
                                    setUpcomingPayment: setAuthenticatedUserUpcomingTransfer,
                                    upcomingPayment: authenticatedUserUpcomingTransfer,
                                }}
                            >
                                <AuthContextProvider loadedTranslations={loadedTranslations}>
                                    <ThemeProvider theme={themesToBeSent}>
                                        <Head>
                                            {typeof window !== 'undefined' && (
                                                <title>{window.PROGRAM?.name} Portal</title>
                                            )}
                                            <link
                                                rel="preload"
                                                href="https://storage.googleapis.com/portal-web-resources/Default%20Website/Generic/Default%20Website_Generic_F4GI_generic_background.webp"
                                                as="image"
                                            />
                                            <link
                                                href="https://fonts.googleapis.com/icon?family=Material+Icons"
                                                rel="preconnect"
                                                as="style"
                                            />
                                            {typeof window !== 'undefined' &&
                                                MOBILE_APP_INSTALL_ENABLED.includes(programFragment) &&
                                                isMobile && (
                                                    <link rel="manifest" href={`/${programFragment}/manifest.json`} />
                                                )}
                                            <link
                                                href="https://fonts.googleapis.com/icon?family=Material+Icons"
                                                rel="stylesheet"
                                            />
                                            <link rel="icon" href="/favicon.ico" />
                                            <meta property="og:image" content="/favicon.ico" />
                                            <meta
                                                name="viewport"
                                                id="viewport"
                                                content="width=device-width,minimum-scale=1.0,maximum-scale=1.0,initial-scale=1.0"
                                            ></meta>
                                            <meta
                                                name="description"
                                                content="Cash transfers from Fund for Guaranteed Income"
                                            ></meta>
                                            <link rel="apple-touch-icon" sizes="512x512" href="/touch-icon.png" />
                                            <meta name="apple-mobile-web-app-status-bar" content="#90cdf4" />
                                            <meta name="google" content="notranslate" />
                                            <script
                                                type="text/javascript"
                                                src="https://assets.calendly.com/assets/external/widget.js"
                                            ></script>
                                        </Head>
                                        {hasLoadedFonts && (
                                            <GlobalWrapper translate="no">
                                                <SIdleTimer />
                                                {!hasLoadedTranslations && (
                                                    <SLoadingIndicator
                                                        color={theme.colors?.primaryColor || SColors.BlackLabelFont}
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            right: 0,
                                                            bottom: 0,
                                                            left: 0,
                                                            margin: 'auto',
                                                        }}
                                                    />
                                                )}
                                                <PlausibleProviderWrapper>
                                                    {hasLoadedTranslations && <Component {...pageProps} />}
                                                </PlausibleProviderWrapper>

                                                {MOBILE_APP_INSTALL_ENABLED.includes(programFragment) && (
                                                    <SInstallMobile />
                                                )}
                                            </GlobalWrapper>
                                        )}
                                        <ConditionalRender
                                            if={process.env.NEXT_PUBLIC_ENVIRONMENT_NAME === 'development'}
                                        >
                                            <SFlex
                                                flexDirection="row"
                                                alignContent="center"
                                                alignItems="center"
                                                justifyContent="center"
                                                sx={{
                                                    position: 'absolute',
                                                    top: '0%',
                                                    right: '0',
                                                    background: 'red',
                                                    width: '100%',
                                                    fontSize: '20px',
                                                    zIndex: 9999,
                                                }}
                                            >
                                                <span>DEV ENVIRONMENT</span>
                                                <Select
                                                    defaultValue={devEnvLinks.find(
                                                        (itm) =>
                                                            localStorage.getItem(LocalStorageKeys.GRAPHQL_ENDPOINT) ===
                                                            itm.value
                                                    )}
                                                    placeholder="Select Preferred Dev Env"
                                                    options={devEnvLinks}
                                                    styles={{
                                                        container: (provided, state) => ({
                                                            width: '200px',
                                                        }),
                                                    }}
                                                    onChange={onEndpointChange}
                                                />
                                            </SFlex>
                                        </ConditionalRender>
                                        <Intercom color={theme.colors?.primaryColor || '#202020'} />
                                        <SGlobalStyles />
                                    </ThemeProvider>
                                </AuthContextProvider>
                            </UserContext.Provider>
                        </OrganizationContext.Provider>
                    </ApolloProvider>
                </LiveChatLoaderProvider>
            </ErrorBoundary>
        )
    } else {
        return null
    }
}

export default appWithTranslation(MyApp)
